/*
  Flexify Theme — collection.js
  Filtering, sorting and pagination for both the collection page and the
  search page. Vanilla JS, no dependencies.

  Both sections expose the same hooks, so this one file drives both:
    [data-section-id]          section root — used by the Section Rendering API
    [data-collection-results]  the swappable results region
    [data-product-grid]        the grid that Load More appends into
    [data-collection-toolbar]  count + sort, re-rendered so the count stays true
    [data-filters-panel]       facets panel, re-rendered so filter counts stay true
*/

(() => {
  const resultsContainer = document.querySelector('[data-collection-results]');
  if (!resultsContainer) return;

  /* The section root carries data-section-id. Shopify's own generated
     .shopify-section wrapper does not, so resolving the id by class name
     silently yields undefined and every request degrades to a full page
     load. Walk up from the results region instead — that element is always
     inside the section root on both templates. */
  const sectionRoot = resultsContainer.closest('[data-section-id]');
  const sectionId = sectionRoot ? sectionRoot.dataset.sectionId : null;

  const filtersPanel = document.querySelector('[data-filters-panel]');
  const basePath = window.location.pathname;

  /* Screen readers get no signal from an innerHTML swap, so results changes
     are announced explicitly. Created here rather than in Liquid so the
     markup stays identical across both sections. */
  const liveRegion = document.createElement('p');
  liveRegion.className = 'visually-hidden';
  liveRegion.setAttribute('role', 'status');
  liveRegion.setAttribute('aria-live', 'polite');
  document.body.appendChild(liveRegion);

  function announce(message) {
    liveRegion.textContent = '';
    window.setTimeout(() => {
      liveRegion.textContent = message;
    }, 100);
  }

  function withSectionId(url) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}section_id=${encodeURIComponent(sectionId)}`;
  }

  async function fetchDocument(url) {
    const response = await fetch(withSectionId(url));
    if (!response.ok) throw new Error(`Request failed: ${response.status}`);
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }

  function swapRegion(doc, selector, current) {
    const incoming = doc.querySelector(selector);
    if (incoming && current) current.innerHTML = incoming.innerHTML;
  }

  function productCount() {
    const grid = document.querySelector('[data-product-grid]');
    return grid ? grid.children.length : 0;
  }


  /* The sticky toolbar paints a strip over the offset left by a hidden header,
     but only while it is actually stuck — otherwise the strip floats over the
     content above it. CSS cannot detect a stuck sticky element, so a zero-height
     sentinel just above the toolbar is observed instead. */
  (() => {
    const sentinel = document.querySelector('[data-toolbar-sentinel]');
    const toolbar = document.querySelector('[data-collection-toolbar]');
    if (!sentinel || !toolbar || !('IntersectionObserver' in window)) return;

    const offset =
      parseInt(
        getComputedStyle(document.documentElement).getPropertyValue('--neroli-sticky-offset'),
        10
      ) || 0;

    new IntersectionObserver(
      ([entry]) => toolbar.classList.toggle('is-stuck', !entry.isIntersecting),
      { rootMargin: `-${offset + 1}px 0px 0px 0px`, threshold: 0 }
    ).observe(sentinel);
  })();

  /* ---- Mobile filter drawer ----
     The same element is the desktop sidebar and the mobile drawer, so the
     dialog semantics are applied on open and removed on close rather than
     baked into the markup — role="dialog" on a static desktop sidebar would
     be a lie to assistive tech. */

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const overlay = document.querySelector('[data-filters-overlay]');
  let drawerReturnFocus = null;

  function isDesktop() {
    return window.matchMedia('(min-width: 990px)').matches;
  }

  function drawerKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeFilterDrawer();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = Array.from(filtersPanel.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null
    );
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function openFilterDrawer(trigger) {
    filtersPanel.classList.add('is-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    if (isDesktop()) return;

    drawerReturnFocus = trigger || null;
    filtersPanel.setAttribute('role', 'dialog');
    filtersPanel.setAttribute('aria-modal', 'true');
    if (overlay) overlay.hidden = false;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', drawerKeydown);

    const close = filtersPanel.querySelector('[data-close-filters]');
    if (close) close.focus();
  }

  function closeFilterDrawer() {
    if (!filtersPanel || !filtersPanel.classList.contains('is-open')) return;

    filtersPanel.classList.remove('is-open');
    filtersPanel.removeAttribute('role');
    filtersPanel.removeAttribute('aria-modal');
    if (overlay) overlay.hidden = true;
    document.body.style.overflow = '';
    document.removeEventListener('keydown', drawerKeydown);

    document.querySelectorAll('[data-open-filters]').forEach((button) =>
      button.setAttribute('aria-expanded', 'false')
    );

    // Returning focus to the trigger is what stops a keyboard user being
    // dumped at the top of the document every time they close the drawer.
    if (drawerReturnFocus && document.contains(drawerReturnFocus)) {
      drawerReturnFocus.focus();
    }
    drawerReturnFocus = null;
  }

  /* Resizing past the breakpoint with the drawer open would otherwise leave
     the page scroll-locked with no visible way to release it. */
  window.addEventListener('resize', () => {
    if (isDesktop()) closeFilterDrawer();
  }, { passive: true });

  /* ---- Applying filters and sort ---- */

  async function fetchAndSwap(url, pushState = true) {
    if (!sectionId) {
      window.location.href = url;
      return;
    }

    resultsContainer.setAttribute('aria-busy', 'true');
    if (filtersPanel) filtersPanel.setAttribute('aria-busy', 'true');

    try {
      const doc = await fetchDocument(url);

      swapRegion(doc, '[data-collection-results]', resultsContainer);
      swapRegion(doc, '[data-filters-panel]', filtersPanel);
      swapRegion(
        doc,
        '[data-collection-toolbar]',
        document.querySelector('[data-collection-toolbar]')
      );

      if (pushState) {
        window.history.pushState({ neroliCollection: true }, '', url);
      }

      restoreViewMode();
      document.dispatchEvent(new CustomEvent('neroli:facets:rendered'));
      document.dispatchEvent(new CustomEvent('neroli:products:rendered'));
      announce(`${productCount()} products shown`);
    } catch (error) {
      window.location.href = url;
    } finally {
      resultsContainer.removeAttribute('aria-busy');
      if (filtersPanel) filtersPanel.removeAttribute('aria-busy');
    }
  }

  window.addEventListener('facets:update', (event) => {
    if (event.detail && event.detail.url) fetchAndSwap(event.detail.url);
  });

  /* Back/forward after filtering. Re-fetches rather than trusting bfcache,
     because pushState only ever changed the query string — the document in
     the history entry still holds the previous result set. The pathname
     guard leaves genuine cross-page navigation to the browser. */
  window.addEventListener('popstate', () => {
    if (window.location.pathname !== basePath) return;
    fetchAndSwap(window.location.pathname + window.location.search, false);
  });

  /* ---- Load more ---- */

  async function appendNextPage(url) {
    if (!sectionId) {
      window.location.href = url;
      return;
    }

    const currentGrid = document.querySelector('[data-product-grid]');
    const currentPagination = document.querySelector('[data-pagination]');
    const trigger = currentPagination
      ? currentPagination.querySelector('[data-load-more]')
      : null;

    if (trigger) {
      trigger.classList.add('is-loading');
      trigger.setAttribute('aria-busy', 'true');
    }

    try {
      const doc = await fetchDocument(url);

      const incomingGrid = doc.querySelector('[data-product-grid]');
      const countBefore = currentGrid ? currentGrid.children.length : 0;

      if (incomingGrid && currentGrid) {
        currentGrid.insertAdjacentHTML('beforeend', incomingGrid.innerHTML);
      }

      /* Replacing the pagination block destroys the button that was focused,
         which would drop keyboard focus to <body> and lose the reading
         position. Move focus to the replacement button, or to the last card
         appended when this was the final page. */
      const incomingPagination = doc.querySelector('[data-pagination]');
      if (currentPagination) {
        if (incomingPagination) {
          currentPagination.replaceWith(incomingPagination);
          const nextTrigger = incomingPagination.querySelector('[data-load-more]');
          if (nextTrigger) nextTrigger.focus();
        } else {
          currentPagination.remove();
          const lastCard = currentGrid ? currentGrid.lastElementChild : null;
          if (lastCard) {
            lastCard.setAttribute('tabindex', '-1');
            lastCard.focus();
          }
        }
      }

      document.dispatchEvent(new CustomEvent('neroli:products:rendered'));

      const added = (currentGrid ? currentGrid.children.length : 0) - countBefore;
      announce(`${added} more products loaded. ${productCount()} products shown.`);
    } catch (error) {
      window.location.href = url;
    }
  }

  /* ---- Delegated handlers ----
     Delegated rather than bound, because the toolbar, pagination and facets
     panel are all replaced by innerHTML swaps above. Direct bindings would
     survive exactly one filter change. */

  document.addEventListener('click', (event) => {
    const loadMore = event.target.closest('[data-load-more]');
    if (loadMore) {
      event.preventDefault();
      appendNextPage(loadMore.getAttribute('href'));
      return;
    }

    const facetLink = event.target.closest('[data-facets-ajax-link]');
    if (facetLink) {
      event.preventDefault();
      fetchAndSwap(facetLink.getAttribute('href'));
      return;
    }

    const filterToggle = event.target.closest('[data-open-filters]');
    if (filterToggle && filtersPanel) {
      /* On desktop this button only appears once the sidebar has been hidden,
         so its job there is to bring the sidebar back rather than to open the
         mobile drawer. */
      const shell = document.querySelector('[data-collection-shell]');
      if (isDesktop() && shell && shell.classList.contains('filters-hidden')) {
        shell.classList.remove('filters-hidden');
        return;
      }
      openFilterDrawer(filterToggle);
      return;
    }

    if (event.target.closest('[data-close-filters]') || event.target.closest('[data-filters-overlay]')) {
      closeFilterDrawer();
      return;
    }

    /* Grid / list view. Persisted so the choice survives a filter change,
       which re-renders the toolbar and would otherwise reset the buttons. */
    const viewButton = event.target.closest('[data-view-mode]');
    if (viewButton) {
      applyViewMode(viewButton.dataset.viewMode);
      try {
        window.localStorage.setItem('neroli:collection-view', viewButton.dataset.viewMode);
      } catch (error) {
        /* Private browsing can refuse storage; the toggle still works for
           this page view, it just won't be remembered. */
      }
    }
  });

  function applyViewMode(mode) {
    const grid = document.querySelector('[data-product-grid]');
    if (grid) grid.classList.toggle('collection-grid--list', mode === 'list');
    document.querySelectorAll('[data-view-mode]').forEach((button) => {
      const active = button.dataset.viewMode === mode;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });
  }

  function restoreViewMode() {
    let stored = null;
    try {
      stored = window.localStorage.getItem('neroli:collection-view');
    } catch (error) {
      stored = null;
    }
    if (stored) applyViewMode(stored);
  }

  restoreViewMode();
})();
